'use client';
import React, { ChangeEvent, FC, useState } from 'react';
import toast from 'react-hot-toast';

import { TeamMemberType } from '@/app/[lang]/(web)/about-us/components/OurTeam';
import { POSITIONS } from '@/app/[lang]/(web)/about-us/components/OurTeam/TeamMember';
import { SendMessage } from '@/app/[lang]/(web)/contact-us/components/ContactUsForm';
import Image from '@/components/core/Image';
import Input from '@/components/core/Input';
import Modal from '@/components/core/Modal';
import PhoneInput from '@/components/core/PhoneInput';
import { validatePhone } from '@/components/core/PhoneInput/validatePhone';
import Toaster from '@/components/popups/Toaster';
import { useTranslation } from '@/hooks/useTranslations';

import { validateForm } from '../MeetingBook/validationFunction';

interface ContactMeProps {
  member?: TeamMemberType;
  onClose: () => void;
}

const ContactMe: FC<ContactMeProps> = ({ member, onClose }) => {
  const [formData, setFormData] = useState({
    name_surname: '',
    email: '',
    phone: '',
    message: '',
    id: member?.id,
  });

  const [errors, setErrors] = useState({
    name_surname: '',
    email: '',
    phone: '',
    message: '',
  });

  const { t } = useTranslation();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setErrors((prev) => ({ ...prev, [event.target.name]: '' }));
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handlePhoneChange = (phone: string) => {
    setErrors((prev) => ({ ...prev, phone: '' }));
    setFormData((prev) => ({ ...prev, phone }));
  };

  const onSubmit = async () => {
    const validation = { ...validateForm(formData), phone: validatePhone(formData.phone) };
    if (Object.values(validation).filter(Boolean).length) {
      return setErrors(validation);
    }

    return await SendMessage({ ...formData, id: member?.id }, 'team')
      .then(() => {
        setFormData({
          name_surname: '',
          email: '',
          phone: '',
          message: '',
          id: member?.id,
        });
        window.dataLayer?.push?.({
          event: 'form_submission',
          form_class: `contact_me_${member?.firstname}`,
          form_id: 'contact_me',
        });
        toast(<Toaster type="success" description="Messagesent-Text" />, {
          duration: 3000,
        });
      })
      .then(onClose);
  };

  if (!member) return null;

  return (
    <Modal
      onClose={onClose}
      title={t('Contactus-SalesTeam-Contactme-Button')}
      showModal={Boolean(member)}
      description={t('Contactus-SalesTeam-Contactme-Paragraph')}
    >
      <div className="popupContactMeContent">
        <div className="popupContactMeTop">
          <div className="popupContactMeTopImgWrp">
            <Image
              src={member.logo}
              width={500}
              height={500}
              alt=""
              style={{
                backgroundColor: member.bgColor ?? 'transparent',
              }}
            />
          </div>
          <div className="popupContactMeTopInfo">
            <p className="popupContactMeTopTitle">{member.firstname}</p>
            <p className="popupContactMeTopTxt">
              {t(POSITIONS[member.position as keyof typeof POSITIONS] || 'member.position')}
            </p>
          </div>
        </div>
        <div className="popupContactMeFormWrp">
          <Input
            name="name_surname"
            onChange={handleChange}
            placeholder={t('Contactus-Form-Name')}
            className="popOkInput darkBg"
            value={formData.name_surname}
          />
          {errors.name_surname && <p className="error-message">{errors.name_surname}</p>}
          <Input
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t('Contactus-Form-Email')}
            className="popOkInput darkBg"
          />
          {errors.email && <p className="error-message">{errors.email}</p>}
          <PhoneInput
            value={formData.phone}
            onChange={handlePhoneChange}
            placeholder={t('Contactus-Form-Phonenumber')}
            className="darkBg"
            hasError={!!errors.phone}
          />
          {errors.phone && <p className="error-message">{errors.phone}</p>}
          <textarea
            name="message"
            value={formData.message}
            placeholder={t('Contactus-Form-Message')}
            className="popOkTextarea type2"
            onChange={(event) => handleChange(event as any)}
          />
          {errors.message && <p className="error-message">{errors.message}</p>}
        </div>
        <div className="contactUsBtnBlock">
          <div className="contactUsBtnBlockInner">
            <button
              onClick={onSubmit}
              className="button btnTypeBig btnTypeOrange fullWidth"
              id={`${member.firstname.toLowerCase()}-form-send`}
            >
              <p>{t('Contactus-Form-Button-Send')}</p>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ContactMe;
